import React from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import { SelectInputProps } from '@mui/material/Select/SelectInput';

export type Option = {
  label: string;
  value: string;
};

type MySelectProps<T> = Omit<SelectProps<T>, 'onChange'> & {
  value: T | '';
  onChange: (value: any) => void;
  inputSx?: React.CSSProperties;
  options: Option[];
  helperText?: string | React.ReactNode;
  subLabel?: string;
  labelColor?: string;
};

const MySelect = <T,>(props: MySelectProps<T>) => {
  const {
    label = '',
    className = '',
    value,
    onChange,
    sx = {},
    inputSx = {},
    options,
    error = false,
    helperText = '',
    disabled = false,
    placeholder = '',
    startAdornment,
    endAdornment,
    subLabel,
    MenuProps,
    labelColor = 'text-black',
    ...otherProps
  } = props;

  const handleChange: SelectInputProps<T>['onChange'] = (event) => {
    onChange(event.target.value);
  };

  return (
    <Box className={`flex flex-col ${className}`} sx={sx}>
      {(label || subLabel) && (
        <div className="mb-2 tablet:mb-3 flex flex-col">
          {label && (
            <label
              className={`font-semibold ${labelColor} text-sm leading-5.5`}
            >
              {label}
            </label>
          )}
          {subLabel && (
            <label className="text-xs mt-1 text-gray-600">{subLabel}</label>
          )}
        </div>
      )}
      <Select<T>
        value={value || ''}
        onChange={handleChange}
        className="!text-sm !leading-5.5 h-14 bg-slate-100 tablet:bg-slate-50 tablet:hover:bg-slate-100"
        sx={{
          '&.MuiSvgIcon-root, .MuiSelect-icon': {
            marginRight: '8px',
          },
          color: error ? '#b91c1c' : '#000',
          fieldset: {
            border: 'none',
          },
          ...inputSx,
        }}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        disabled={disabled}
        displayEmpty
        MenuProps={MenuProps}
        fullWidth={true}
        renderValue={
          value
            ? undefined
            : () => (
                <p className="leading-5.5 text-sm opacity-[0.42]">
                  {placeholder}
                </p>
              )
        }
        {...otherProps}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              width: '100%',
              color: '#334155',
              fontSize: '14px',
              lineHeight: 1.5,
              ':hover': {
                backgroundColor: '#dbeafe',
                color: '#334155',
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {error && helperText && (
        <span className="text-red-700 text-xs mt-2">{helperText}</span>
      )}
    </Box>
  );
};

export default MySelect;
